/**
 * Hyperliquid Exchange Client
 *
 * POST /exchange endpoint for trading operations (orders, cancels, leverage, etc.).
 * Unlike /info (read-only), /exchange requires EIP-712 signatures.
 *
 * All trading operations go through postExchange() which handles the
 * action + nonce + signature envelope format.
 */

import { HL_MAINNET_API, HL_TESTNET_API } from "./constants";
import type { HLSignature } from "./types";

// ============================================================================
// Response Types
// ============================================================================

/** Status object in order response */
export interface HLOrderStatus {
  resting?: { oid: number };
  filled?: { totalSz: string; avgPx: string; oid: number };
  error?: string;
}

/** Response from order placement */
export interface HLOrderResponse {
  status: "ok" | "err";
  response?: {
    type: "order";
    data?: {
      statuses: HLOrderStatus[];
    };
  };
}

/** Response from cancel operation */
export interface HLCancelResponse {
  status: "ok" | "err";
  response?: {
    type: "cancel";
    data?: {
      statuses: string[];
    };
  };
}

/** Generic exchange response (for approvals, leverage, etc.) */
export interface HLExchangeResponse {
  status: "ok" | "err";
  response?: {
    type: string;
    error?: string;
  };
}

// ============================================================================
// Exchange Client
// ============================================================================

export interface HLExchangeClientOptions {
  testnet?: boolean;
}

/**
 * Post an action to the Hyperliquid /exchange endpoint.
 *
 * @param action - The action object (order, cancel, approveAgent, etc.)
 * @param nonce - Timestamp nonce used in signing
 * @param signature - EIP-712 signature { r, s, v }
 * @param options - Client options (testnet flag)
 */
export async function postExchange<T = HLExchangeResponse>(
  action: Record<string, unknown>,
  nonce: number,
  signature: HLSignature,
  options: HLExchangeClientOptions = {},
): Promise<T> {
  const baseUrl = options.testnet ? HL_TESTNET_API : HL_MAINNET_API;

  const response = await fetch(`${baseUrl}/exchange`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action,
      nonce,
      signature,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Hyperliquid exchange error: ${response.status} ${response.statusText}`,
    );
  }

  return response.json() as Promise<T>;
}

// ============================================================================
// Result Parsing
// ============================================================================

/** Parse an order response into a simple success/error result. */
export function parseOrderResult(response: HLOrderResponse): {
  success: boolean;
  orderId?: number;
  error?: string;
} {
  if (response.status !== "ok" || !response.response?.data?.statuses[0]) {
    return { success: false, error: "Unexpected response" };
  }

  const status = response.response.data.statuses[0];

  if (status.resting) {
    return { success: true, orderId: status.resting.oid };
  }
  if (status.filled) {
    return { success: true, orderId: status.filled.oid };
  }
  if (status.error) {
    return { success: false, error: status.error };
  }

  return { success: false, error: "Unknown order status" };
}
